// Typed donation endpoint functions. Backed by GET /api/v1/donations/*.
//
// Per CHG-1243 plan — consumed by @generated/screens/donation-list.ts +
// donation-detail.ts queryFn callbacks. Spec:
// specs/tier-04-interface/donation-engine.api.yaml.
//
// /donations/{id}/refunds and /donations/{id}/audit are NOT yet canonical
// v4 endpoints — the v2 client wraps them as bridge fetches of
// /donations/{id}/timeline filtered by kind. Forward-compatible: when
// canonical endpoints ship, swap the internal URLs without page-code change.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";
import type {
  Donation,
  DonationReceipt,
  DonationRefund,
  DonationAuditEntry,
  PagedEnvelope,
} from "./types";

export interface ListDonationsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  channel?: string;
  designation_id?: string;
  // CHG-1668 / ITER-3777 — generic filters bag forwarded from
  // ListPage filter chips (status + payment_method + designation_id).
  filters?: Record<string, string | number | boolean | undefined>;
}

export async function listDonations(
  params: ListDonationsParams = {},
): Promise<NormalizedList<Donation>> {
  const filters = params.filters ?? {};
  const r = await apiClient().get<RawListEnvelope<Donation>>("/donations", {
    // CHG-2916 / ITER-905655 — the /donations router
    // (src/api/app/routers/donations.py:list_donations) reads 1-based
    // `page` / `per_page` / `search` (NOT offset/limit/q), has NO `sort`
    // param, and filters on `source` (DonationSource) — there is NO
    // `channel` query param, so the prior `channel:` send was a dropped
    // no-op. We forward only the params the router actually backs:
    // status / designation_id / payment_method. `channel` (a row field,
    // not a filter) is intentionally not sent.
    params: {
      ...listPageParams(params),
      // Filter-bag overrides explicit param when both are set so the
      // chip-driven UI is the source of truth post-CHG-1668.
      status: filters.status ?? params.status,
      designation_id: filters.designation_id ?? params.designation_id,
      payment_method: filters.payment_method,
    },
  });
  return normalizeListResponse<Donation>(r.data);
}

export async function getDonation(id: string): Promise<Donation> {
  const r = await apiClient().get<Donation>(`/donations/${id}`);
  return r.data;
}

export async function getDonationReceipt(id: string): Promise<DonationReceipt> {
  const r = await apiClient().get<DonationReceipt>(`/donations/${id}/receipt`);
  return r.data;
}

// /donations/{id}/refunds bridge — uses /timeline?kind=refund until the
// canonical endpoint ships.
export async function getDonationRefunds(
  id: string,
): Promise<PagedEnvelope<DonationRefund>> {
  const r = await apiClient().get<PagedEnvelope<DonationRefund>>(
    `/donations/${id}/timeline`,
    { params: { kind: "refund" } },
  );
  return r.data;
}

// /donations/{id}/audit bridge — uses /timeline?kind=audit until the
// canonical endpoint ships.
export async function getDonationAudit(
  id: string,
): Promise<DonationAuditEntry[]> {
  const r = await apiClient().get<DonationAuditEntry[]>(
    `/donations/${id}/timeline`,
    { params: { kind: "audit" } },
  );
  return r.data;
}

export const donationsApi = {
  listDonations,
  getDonation,
  getDonationReceipt,
  getDonationRefunds,
  getDonationAudit,
};
